import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { supabase } from "@/lib/agents/db"
import { MFAChallenge } from "@/components/mfa-challenge"

export function Login() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false)
  const [mfaChallenge, setMfaChallenge] = useState<{ challengeId: string; factors: any[] } | null>(null)
  const navigate = useNavigate()
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        toast({
          title: "Error",
          description: "Invalid email or password",
          variant: "destructive",
        })
        return
      }

      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()

      if (aalData?.currentLevel === "aal1" && aalData?.nextLevel === "aal2") {
        const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors()

        if (factorsError) {
          toast({
            title: "Error",
            description: "Failed to check MFA status",
            variant: "destructive",
          })
          return
        }

        const verifiedFactors = [...(factorsData.totp || []), ...(factorsData.phone || [])].filter(
          (factor) => factor.status === "verified"
        )

        if (verifiedFactors.length > 0) {
          const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
            factorId: verifiedFactors[0].id,
          })

          if (challengeError) {
            toast({
              title: "Error",
              description: "Failed to initiate MFA challenge",
              variant: "destructive",
            })
            return
          }

          setMfaChallenge({
            challengeId: challengeData.id,
            factors: verifiedFactors,
          })
          return
        }
      }

      navigate("/")
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) {
        toast({
          title: "Error",
          description: "Failed to send reset email",
          variant: "destructive",
        })
      } else {
        toast({
          title: "Success",
          description: "Password reset email sent",
        })
        setForgotPasswordMode(false)
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative h-screen w-full overflow-hidden">
      {/* Login Form Overlay */}
      <div className="relative z-10 flex h-full items-center justify-center">
        {mfaChallenge ? (
          <MFAChallenge
            challengeId={mfaChallenge.challengeId}
            factors={mfaChallenge.factors}
            onSuccess={() => navigate("/")}
            onCancel={() => {
              setMfaChallenge(null)
              supabase.auth.signOut()
            }}
          />
        ) : (
          <Card className="w-[350px] bg-white/95 backdrop-blur-md border-2 border-gray-200/50 shadow-2xl shadow-black/40 transform hover:scale-[1.02] transition-all duration-300">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <img
                  src="/signpost ai logo black@4x (1) (5).png"
                  alt="SignpostAI Logo"
                  className="h-12 w-auto"
                />
              </div>
              <CardTitle>Login</CardTitle>
              <CardDescription>
                Enter your credentials to access your account
              </CardDescription>
            </CardHeader>
            <form onSubmit={forgotPasswordMode ? handleForgotPassword : handleSubmit}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                {!forgotPasswordMode && (
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex flex-col space-y-2">
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Logging in..." : forgotPasswordMode ? "Send Reset Email" : "Login"}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  className="w-full"
                  onClick={() => setForgotPasswordMode(!forgotPasswordMode)}
                >
                  {forgotPasswordMode ? "Back to Login" : "Forgot Password?"}
                </Button>
              </CardFooter>
            </form>
          </Card>
        )}
      </div>
    </div>
  )
} 